import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAccountManagementTasks,
  completeAccountManagementTask,
  rescheduleAccountManagementTask,
  cancelAccountManagementTask,
  triggerAccountManagementExecution,
  getAccountManagementComments,
  createAccountManagementComment,
  updateAccountManagementComment,
  transferCsmLead,
  getCsmTalentComments,
} from "../api/account-management-api";
import {
  CreateAccountManagementCommentRequest,
  UpdateAccountManagementCommentRequest,
  TransferCsmLeadRequest,
} from "../models/AccountManagement";
import { useSnackbarContext } from "../contexts/useSnackBarContext";

// ===========================
// TASKS
// ===========================

export const useAccountManagementTasks = (projectId: number) => {
  return useQuery({
    queryKey: ["account-management-tasks", projectId],
    queryFn: () => getAccountManagementTasks(projectId),
    enabled: projectId > 0,
  });
};

export const useCompleteAccountManagementTask = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbarContext();

  return useMutation({
    mutationFn: ({
      projectId,
      taskId,
    }: {
      projectId: number;
      taskId: number;
    }) => completeAccountManagementTask(projectId, taskId),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ["account-management-tasks", projectId],
      });
      showSuccess("accountManagement.taskCompleted");
    },
    onError: (error: Error) => {
      console.error("Failed to complete account management task:", error);
      showError("accountManagement.taskCompletionError");
    },
  });
};

export const useRescheduleAccountManagementTask = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbarContext();

  return useMutation({
    mutationFn: ({
      projectId,
      taskId,
      newDate,
    }: {
      projectId: number;
      taskId: number;
      newDate: string; // ISO 8601
    }) => rescheduleAccountManagementTask(projectId, taskId, newDate),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ["account-management-tasks", projectId],
      });
      showSuccess("accountManagement.taskRescheduled");
    },
    onError: (error: Error) => {
      console.error("Failed to reschedule account management task:", error);
      showError("accountManagement.taskRescheduleError");
    },
  });
};

export const useCancelAccountManagementTask = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbarContext();

  return useMutation({
    mutationFn: ({
      projectId,
      taskId,
    }: {
      projectId: number;
      taskId: number;
    }) => cancelAccountManagementTask(projectId, taskId),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ["account-management-tasks", projectId],
      });
      // Comments invalidation too — once a task is cancelled, the
      // "no available recipient" filter in the comment UI may shift.
      queryClient.invalidateQueries({
        queryKey: ["account-management-comments", projectId],
      });
      showSuccess("accountManagement.taskCancelled");
    },
    onError: (error: Error) => {
      console.error("Failed to cancel account management task:", error);
      showError("accountManagement.taskCancelError");
    },
  });
};

export const useTriggerAccountManagementExecution = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbarContext();

  return useMutation({
    mutationFn: ({ projectId }: { projectId: number }) =>
      triggerAccountManagementExecution(projectId),
    onSuccess: (_, { projectId }) => {
      // The trigger is fire-and-forget: the worker runs asynchronously and flips statuses
      // (Planned -> Sent/Completed) a few seconds later. Invalidate now AND once more after
      // a short delay so the table reflects the new statuses without a manual reload. If the
      // statuses don't change, the trigger failed (logged server-side) and the CSM re-clicks.
      const invalidate = () =>
        queryClient.invalidateQueries({
          queryKey: ["account-management-tasks", projectId],
        });
      invalidate();
      setTimeout(invalidate, 4000);
      showSuccess("accountManagement.executionTriggered");
    },
    onError: (error: Error) => {
      console.error("Failed to trigger account management execution:", error);
      showError("accountManagement.executionTriggerError");
    },
  });
};

// ===========================
// COMMENTS
// ===========================

export const useAccountManagementComments = (projectId: number) => {
  return useQuery({
    queryKey: ["account-management-comments", projectId],
    queryFn: () => getAccountManagementComments(projectId),
    enabled: projectId > 0,
  });
};

export const useCreateAccountManagementComment = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbarContext();

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: number;
      data: CreateAccountManagementCommentRequest;
    }) => createAccountManagementComment(projectId, data),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ["account-management-comments", projectId],
      });
      // Also invalidate tasks since a comment can complete a task
      queryClient.invalidateQueries({
        queryKey: ["account-management-tasks", projectId],
      });
      // Invalidate application notes too: CsmTalentComment is dispatched to ApplicationNote
      // and shown on the freelancer profile (queryKey prefix "notes").
      queryClient.invalidateQueries({ queryKey: ["notes"] });
      // Invalidate CSM talent comments shown in the AM tab too.
      queryClient.invalidateQueries({
        queryKey: ["csm-talent-comments", projectId],
      });
      showSuccess("accountManagement.commentCreated");
    },
    onError: (error: Error) => {
      console.error("Failed to create account management comment:", error);
      showError("accountManagement.commentCreationError");
    },
  });
};

export const useUpdateAccountManagementComment = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbarContext();

  return useMutation({
    mutationFn: ({
      projectId,
      commentId,
      data,
    }: {
      projectId: number;
      commentId: number;
      data: UpdateAccountManagementCommentRequest;
    }) => updateAccountManagementComment(projectId, commentId, data),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ["account-management-comments", projectId],
      });
      showSuccess("accountManagement.commentUpdated");
    },
    onError: (error: Error) => {
      console.error("Failed to update account management comment:", error);
      showError("accountManagement.commentUpdateError");
    },
  });
};

// Note: deletion is intentionally not exposed.
// Once a comment is created, the linked AM task is marked as Completed.
// Allowing deletion would create an inconsistency (task Completed but no comment).
// To revise a comment, use UpdateCommentAsync.

// ===========================
// CSM LEAD
// ===========================

// Note: snackbar feedback for this mutation is intentionally NOT in the hook.
// ProjectContacts handles success/error via its own setAlertMessage pattern (consistent
// with the other operations in that component); duplicating it here would double-notify.
export const useTransferCsmLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: number;
      data: TransferCsmLeadRequest;
    }) => transferCsmLead(projectId, data),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
};

// "Commentaire CSM sur le talent" notes for a project.
// Stored as ApplicationNote (with ProjectId) and displayed both on the freelancer
// profile (via ApplicationNotes component) and in the AM tab (via this hook).
export const useCsmTalentComments = (projectId: number) => {
  return useQuery({
    queryKey: ["csm-talent-comments", projectId],
    queryFn: () => getCsmTalentComments(projectId),
    enabled: projectId > 0,
  });
};
